import { Button } from "@ameelio/ui";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import {
  Box,
  CardContent,
  CircularProgress,
  Fade,
  Typography,
  useMediaQuery as measureScreenWidth,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { List, RowComponentProps, useListRef } from "react-window";
import { SystemUserStatus } from "../api/graphql";
import { IconSkeleton } from "../lib/closet";
import getLanguageDirection from "../lib/languageDir";
import { belowLargeTablet } from "../lib/responsiveHelpers";
import { useCurrentCorrespondent } from "../SessionBoundary";
import {
  GetNotificationsDocument,
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
} from "./GetNotifications.generated";
import { Notification, NotificationSkeleton } from "./Notification";
import NotificationEventType from "./NotificationEventType";
import notificationIcons from "./notificationIcons";

type NotificationNode = Exclude<
  GetNotificationsQuery["currentCorrespondent"],
  null
>["notificationEvents"]["edges"][0]["node"];

export const notificationsPerPage = 20;

// default height for rows that haven't self-measured yet, so the list
// doesn't start at 0px and grow one row at a time as each one mounts
const defaultRowHeight = 100;

type RowProps = {
  notifications: NotificationNode[];
  canLoadMore: boolean;
  loadingMore: boolean;
  currentUser: ReturnType<typeof useCurrentCorrespondent>;
  setHeight: (index: number, height: number) => void;
  onLoadMore: () => void;
  onNavigate: (url: string) => void;
  t: ReturnType<typeof useTranslation>["t"];
};

function NotificationRow({
  ariaAttributes,
  index,
  style,
  notifications,
  canLoadMore,
  loadingMore,
  currentUser,
  setHeight,
  onLoadMore,
  onNavigate,
  t,
}: RowComponentProps<RowProps>) {
  const n = notifications[index];
  const icon = n.object.__typename
    ? notificationIcons[n.verb]?.[n.object.__typename]
    : null;
  const clickUrl = n.object.__typename
    ? {
        Connection:
          "organizationMembership" in n.object &&
          !!n.object.organizationMembership
            ? currentUser.__typename === "Inmate"
              ? `/contacts/${n.object.id}`
              : `/organization/${n.object.organizationMembership.organization.id}/contacts/${n.object.inmate.id}`
            : `/contacts/${n.object.id}`,
        Meeting: `/events/${n.object.id}`,
        Message: `/messages`, // TODO - link to specific message
        Organization: `/organization/${n.object.id}/team`,
        SystemUser:
          n.object.__typename === "SystemUser" &&
          n.object.status === SystemUserStatus.Rejected
            ? `/settings`
            : null,
      }[n.object.__typename]
    : null;

  let notifItem = (
    <Notification
      icon={icon || <IconSkeleton />}
      type={n.type as NotificationEventType}
      params={n.params}
      isRead={n.isRead}
      created={n.createdAt}
      onClick={() => {
        if (clickUrl) {
          onNavigate(clickUrl);
        }
      }}
      index={index}
      setHeight={setHeight}
    />
  );

  if (n.id === notifications[notifications.length - 1].id && canLoadMore) {
    notifItem = (
      <>
        {notifItem}
        <Box
          sx={{
            display: "flex",
            py: 1,
            px: 2,
            cursor: "pointer",
            backgroundColor: n.isRead ? grey[50] : "white",
            justifyContent: "center",
          }}
        >
          {!loadingMore && (
            <Button onClick={onLoadMore}>{t("Load more")}</Button>
          )}
          {loadingMore && <CircularProgress size={30} />}
        </Box>
      </>
    );
  }

  return (
    <div {...ariaAttributes} style={style}>
      {notifItem}
    </div>
  );
}

export function NotificationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationNode[]>();
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [canLoadMore, setCanLoadMore] = useState<boolean>(false);
  const [checkedForNew, setCheckedForNew] = useState<boolean>(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // how much viewport space to maintain below the list
  const isMobileOrSmallTablet = belowLargeTablet(measureScreenWidth);
  const bottomSpace = isMobileOrSmallTablet ? 16 : 64;

  const currentUser = useCurrentCorrespondent();

  const [checkNew] = useLazyQuery(GetNotificationsDocument, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-only",
  });

  const { data, loading, fetchMore } = useQuery<
    GetNotificationsQuery,
    GetNotificationsQueryVariables
  >(GetNotificationsDocument, {
    variables: {
      perPage: notificationsPerPage,
      cursor: undefined,
      after: undefined,
    },
  });

  const listRef = useListRef(null);
  const heightsMap = useRef<Record<number, number>>({});
  const [, forceHeightRecompute] = useReducer((n: number) => n + 1, 0);
  const setHeight = useCallback((index: number, height: number) => {
    if (heightsMap.current[index] === height) return;
    heightsMap.current = { ...heightsMap.current, [index]: height };
    forceHeightRecompute();
  }, []);
  const getSize = (index: number) =>
    heightsMap.current[index] ?? defaultRowHeight;

  const checkNewOnce = useCallback(
    (endCursor: string | undefined) => {
      if (checkedForNew) return;
      checkNew({
        variables: {
          perPage: 999,
          after: endCursor,
          cursor: undefined,
        },
      });
      setCheckedForNew(true);
    },
    [checkNew, checkedForNew, setCheckedForNew]
  );

  useEffect(() => {
    setLoadingMore(false);
    const reversedSet = data?.currentCorrespondent?.notificationEvents.edges
      .map((e) => e.node)
      .filter((r) =>
        // Filter only those that we can manage
        Object.values(NotificationEventType).includes(
          r.type as unknown as NotificationEventType
        )
      )
      .reverse();
    setNotifications(reversedSet);
    setCanLoadMore(
      data?.currentCorrespondent?.notificationEvents?.pageInfo
        ?.hasPreviousPage || false
    );
    const endCursor =
      data?.currentCorrespondent?.notificationEvents?.pageInfo?.endCursor;
    if (endCursor || reversedSet?.length === 0) checkNewOnce(endCursor);
  }, [data, checkNewOnce, setNotifications]);

  const onLoadMore = useCallback(() => {
    setLoadingMore(true);
    fetchMore({
      variables: {
        cursor:
          data?.currentCorrespondent?.notificationEvents.pageInfo.startCursor,
      },
    });
  }, [data, fetchMore]);

  const rowKey = useCallback(
    (index: number, rowProps: RowProps) => rowProps.notifications[index].id,
    []
  );

  return (
    <CardContent
      ref={elementRef}
      sx={{
        whiteSpace: "nowrap",
        padding: "0 !important",
      }}
      dir={getLanguageDirection()}
    >
      {loading && (notifications?.length || 0) < 1 && (
        <Fade
          in={!data}
          timeout={{ enter: 250, exit: 0 }}
          style={{ transitionDelay: "150ms" }}
        >
          <Box>
            <NotificationSkeleton />
            <br />
            <NotificationSkeleton />
            <br />
            <NotificationSkeleton />
            <br />
          </Box>
        </Fade>
      )}
      {!loading && (notifications?.length || 0) < 1 && (
        <Typography
          variant="body1"
          sx={{ textAlign: "center", py: 3, color: "text.secondary" }}
        >
          {t("You do not have any notifications")}
        </Typography>
      )}
      {notifications && (
        <List
          listRef={listRef}
          rowComponent={NotificationRow}
          rowCount={notifications.length}
          rowHeight={(index) => getSize(index)}
          rowKey={rowKey}
          rowProps={{
            notifications,
            canLoadMore,
            loadingMore,
            currentUser,
            setHeight,
            onLoadMore,
            onNavigate: navigate,
            t,
          }}
          style={{
            height: Math.min(
              notifications.reduce((sum, _n, i) => sum + getSize(i), 0),
              (window?.innerHeight || Infinity) -
                // space from container to top of viewport
                (elementRef.current?.getBoundingClientRect().top || 0) -
                // space from container to bottom of viewport
                bottomSpace
            ),
            width: "100%",
            borderRadius: 16,
            direction: getLanguageDirection(),
          }}
        />
      )}
    </CardContent>
  );
}
